import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { billingApi, BillingAccount, BillingTransaction, Invoice } from '../services/api';
import TopUpDialog from './TopUpDialog';
import PaymentMethodsManager from './PaymentMethodsManager';
import AutoTopupSettings from './AutoTopupSettings';
import { formatCurrency } from '../utils/currency';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { currentLocale } from '../i18n';
import '../styles/reports.css';
import '../styles/users.css';
import '../styles/payments.css';

const BillingPage: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const isAdmin = user?.role === 'admin' || user?.role === 'regional_admin';
  const canManualTopup = user?.role === 'admin' || user?.role === 'regional_admin' || user?.role === 'manager';
  // Everyone who owns a balance tops it up themselves. The role 'client' is the
  // exception — an agency's client, whose money the agency handles.
  const canSelfTopup = canManualTopup || ['agency_admin', 'agency_manager', 'agency_buyer',
    'freelancer', 'smb_client'].includes(user?.role || '');
  const [accounts, setAccounts] = useState<BillingAccount[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<string | null>(null);
  const [transactions, setTransactions] = useState<BillingTransaction[]>([]);
  const [txTotal, setTxTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showTopup, setShowTopup] = useState(false);
  const [showCardTopup, setShowCardTopup] = useState(false);
  const [topupAmount, setTopupAmount] = useState('');
  const [topupDesc, setTopupDesc] = useState('');
  const [topupType, setTopupType] = useState<'payment' | 'bonus'>('payment');
  const [topupDate, setTopupDate] = useState(new Date().toISOString().slice(0, 10));
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'transactions' | 'methods' | 'auto-topup'>('transactions');
  const [txTypeFilter, setTxTypeFilter] = useState('');
  const [txStatusFilter, setTxStatusFilter] = useState('');
  const [txDateFrom, setTxDateFrom] = useState('');
  const [txDateTo, setTxDateTo] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [modelFilter, setModelFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [officeFilter, setOfficeFilter] = useState<string>('');
  const [showHidden, setShowHidden] = useState(false);
  const isSuperAdmin = user?.role === 'admin';

  const handleHideToggle = async (a: BillingAccount, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      if (a.hidden) await billingApi.unhideAccount(a.advertiser_id);
      else await billingApi.hideAccount(a.advertiser_id);
      await loadAccounts();
    } catch (err) {
      console.error('Failed to toggle hide', err);
    }
  };

  const loadAccounts = useCallback(async () => {
    try {
      const data = await billingApi.listAccounts();
      setAccounts(data.accounts);
    } catch (e: any) {
      setError(e.response?.data?.detail || t('billing.errLoadAccounts'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  const loadTransactions = useCallback(async (advId: string) => {
    try {
      const data = await billingApi.getTransactions(advId, { limit: 50 });
      setTransactions(data.transactions);
      setTxTotal(data.total);
    } catch (e) {
      console.error('Failed to load transactions', e);
    }
  }, []);

  const loadInvoices = useCallback(async (advId: string) => {
    try {
      const data = await billingApi.listInvoices({ advertiser_id: advId, limit: 200 });
      setInvoices(data.invoices || []);
    } catch (e) {
      console.error('Failed to load invoices', e);
    }
  }, []);

  // Methods + auto-topup tabs handle their own data fetching via the new
  // paymentMethodsApi. We just bump methodsRefreshKey when something changes
  // (e.g., a successful top-up that may have saved a card).
  const [methodsRefreshKey, setMethodsRefreshKey] = useState(0);

  useEffect(() => {
    if (selectedAccount) {
      loadTransactions(selectedAccount);
      loadInvoices(selectedAccount);
    }
  }, [selectedAccount, loadTransactions, loadInvoices]);

  const handleTopup = async () => {
    if (!selectedAccount || !topupAmount) return;
    try {
      const result = await billingApi.topup({
        advertiser_id: selectedAccount,
        amount: parseFloat(topupAmount),
        description: topupDesc || undefined,
        topup_type: topupType,
        topup_date: topupDate,
      });
      setShowTopup(false);
      setTopupAmount('');
      setTopupDesc('');
      setTopupType('payment');
      setTopupDate(new Date().toISOString().slice(0, 10));
      loadAccounts();
      loadTransactions(selectedAccount);
    } catch (e: any) {
      alert(e.response?.data?.detail || t('billing.errTopup'));
    }
  };


  const handleCardCheckoutOpened = () => {
    // For bank-transfer flow the invoice was just created synchronously —
    // refresh the list immediately so the new "Issued" row shows up.
    if (selectedAccount) {
      loadInvoices(selectedAccount);
    }
    // Card flow: webhook needs a few seconds to land; balance + transactions
    // get refreshed once it has.
    setTimeout(() => {
      loadAccounts();
      if (selectedAccount) {
        loadTransactions(selectedAccount);
        loadInvoices(selectedAccount);
      }
      setMethodsRefreshKey(k => k + 1);
    }, 6000);
  };

  // formatCurrency imported from utils/currency

  const txTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      topup: t('billing.txTopup'),
      spend_deduction: t('billing.txSpend'),
      adjustment: t('billing.txAdjustment'),
      refund: t('billing.txRefund'),
    };
    return labels[type] || type;
  };

  const txTypeColor = (type: string) => {
    if (type === 'topup' || type === 'refund') return '#27ae60';
    if (type === 'spend_deduction') return '#e74c3c';
    return '#6c757d';
  };

  const paymentStatusStyle = (status: string): React.CSSProperties => {
    const map: Record<string, { bg: string; color: string }> = {
      succeeded: { bg: 'rgba(39, 174, 96, 0.1)', color: '#27ae60' },
      processing: { bg: 'rgba(41, 128, 185, 0.1)', color: '#2980b9' },
      failed: { bg: 'rgba(231, 76, 60, 0.1)', color: '#e74c3c' },
      canceled: { bg: 'rgba(108, 117, 125, 0.1)', color: '#6c757d' },
      requires_action: { bg: 'rgba(230, 126, 34, 0.1)', color: '#e67e22' },
      created: { bg: 'rgba(108, 117, 125, 0.1)', color: '#6c757d' },
    };
    const s = map[status] || map.created;
    return {
      display: 'inline-block',
      padding: '4px 10px',
      borderRadius: 6,
      fontSize: 12,
      fontWeight: 600,
      background: s.bg,
      color: s.color,
      whiteSpace: 'nowrap' as const,
    };
  };

  const modelBadgeStyle = (model: string): React.CSSProperties => {
    const isPrepaid = model === 'prepaid';
    return {
      display: 'inline-block',
      padding: '4px 10px',
      borderRadius: 6,
      fontSize: 12,
      fontWeight: 600,
      background: isPrepaid ? 'rgba(5, 150, 105, 0.1)' : 'rgba(124, 58, 237, 0.1)',
      color: isPrepaid ? '#059669' : '#7c3aed',
      whiteSpace: 'nowrap' as const,
    };
  };

  // Derive office options from accounts
  const officeOptions = useMemo(() => {
    const offices = new Set(accounts.map(a => a.office_name || 'Central Hub'));
    return Array.from(offices).sort();
  }, [accounts]);

  // Filter accounts
  const filteredAccounts = accounts.filter(a => {
    // Active tab hides hidden accounts; Hidden tab shows only them.
    if (showHidden ? !a.hidden : a.hidden) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const matchName = (a.advertiser_name || '').toLowerCase().includes(q);
      const matchOffice = (a.office_name || 'Central Hub').toLowerCase().includes(q);
      const matchId = a.advertiser_id.toLowerCase().includes(q);
      if (!matchName && !matchOffice && !matchId) return false;
    }
    if (modelFilter && a.billing_model !== modelFilter) return false;
    if (officeFilter && (a.office_name || 'Central Hub') !== officeFilter) return false;
    if (statusFilter === 'paused' && !a.is_paused_by_billing) return false;
    if (statusFilter === 'active' && a.is_paused_by_billing) return false;
    if (statusFilter === 'auto-pause' && !a.auto_pause_enabled) return false;
    return true;
  });

  const hasFilters = searchQuery || modelFilter || statusFilter || officeFilter;

  const clearFilters = () => {
    setSearchQuery('');
    setModelFilter('');
    setStatusFilter('');
    setOfficeFilter('');
  };

  if (loading) {
    return (
      <div className="users-loading">
        <div className="users-spinner" />
      </div>
    );
  }

  const selectedAccountData = accounts.find(a => a.advertiser_id === selectedAccount);

  // Methods + Auto-topup tabs are Stripe-only; hidden from clients/agencies/freelancers
  // while top-ups are processed manually by the internal team.
  const tabItems = [
    { key: 'transactions' as const, label: t('billing.tabTransactions') },
    ...(canSelfTopup
      ? [
          { key: 'methods' as const, label: t('billing.tabCards') },
          { key: 'auto-topup' as const, label: t('billing.tabAutoTopup') },
        ]
      : []),
  ];

  // ── Detail view ──
  if (selectedAccount && selectedAccountData) {
    return (
      <>
        {/* Back + account name */}
        <div className="header">
          <div className="header-title">
            <button
              onClick={() => setSelectedAccount(null)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8 }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              {t('billing.backToAccounts')}
            </button>
            <h1>{selectedAccountData.advertiser_name || selectedAccountData.advertiser_id}</h1>
            <p className="header-subtitle">
              {selectedAccountData.office_name || 'Central Hub'}
              {' · '}
              {t(`billing.bm_${selectedAccountData.billing_model}`,
                { defaultValue: selectedAccountData.billing_model })}
            </p>
          </div>
          <div className="header-actions">
            <div style={{ textAlign: 'right', marginRight: 16 }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: selectedAccountData.balance <= 0 ? '#e74c3c' : '#27ae60', lineHeight: 1.2 }}>
                {formatCurrency(selectedAccountData.balance, selectedAccountData.currency)}
              </div>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{t('billing.currentBalance')}</span>
            </div>
            {canSelfTopup && selectedAccountData?.payment_available && (
              <button
                style={{
                  background: 'var(--accent-gradient)',
                  color: '#fff',
                  border: 'none',
                  padding: '10px 20px',
                  fontSize: 14,
                  borderRadius: 10,
                  cursor: 'pointer',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                }}
                onClick={() => setShowCardTopup(true)}
              >
                {t('billing.topUp')}
              </button>
            )}
            {canSelfTopup && !selectedAccountData?.payment_available && (
              <span
                title={t('billing.noProfileTitle')}
                style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}
              >
                {t('billing.topupUnavailable')}
              </span>
            )}
            {canManualTopup && (
              <button
                className="btn-secondary"
                onClick={() => setShowTopup(true)}
                title={t('billing.manualTopupTitle')}
              >
                {t('billing.manual')}
              </button>
            )}
          </div>
        </div>

        {/* Tab bar */}
        <div className="users-tabs">
          {tabItems.map(tab => (
            <button
              key={tab.key}
              className={`users-tab ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Transactions tab */}
        {activeTab === 'transactions' && (() => {
          const filtered = invoices.filter(inv => {
            if (txTypeFilter && inv.type !== txTypeFilter) return false;
            if (txStatusFilter && inv.status !== txStatusFilter) return false;
            if (txDateFrom && inv.period_start && inv.period_start < txDateFrom) return false;
            if (txDateTo && inv.period_end && inv.period_end > txDateTo) return false;
            return true;
          });
          const sorted = [...filtered].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
          const fmtDate = (iso: string) => {
            const d = new Date(iso);
            return `${d.getDate().toString().padStart(2, '0')}.${(d.getMonth() + 1).toString().padStart(2, '0')}.${d.getFullYear()}`;
          };
          const fmtAmount = (n: number) => {
            const parts = n.toFixed(2).split('.');
            return { whole: parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '\u2009'), cents: parts[1] };
          };
          const getStatusColor = (s: string) => s === 'paid' ? '#27ae60' : s === 'bonus' ? '#2e7d32' : s === 'issued' ? '#2980b9' : s === 'ongoing' ? '#90a4ae' : '#546e7a';
          const getStatusLabel = (s: string) =>
            t(`billing.st_${s}`, { defaultValue: s.charAt(0).toUpperCase() + s.slice(1) });

          const handleDownloadPdf = async (id: number, num: string, type: string) => {
            try {
              const blob = await billingApi.downloadInvoicePdf(id);
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = type === 'payment_confirmation' ? `confirmation_${num}.pdf` : `invoice_${num}.pdf`;
              a.click();
              window.URL.revokeObjectURL(url);
            } catch { alert(t('billing.errDownloadPdf')); }
          };

          const handleDelete = async (id: number, num: string, total: number, type: string, status: string) => {
            const reverses = type === 'payment_confirmation' || (type === 'topup_request' && status === 'paid');
            const tail = t(reverses ? 'billing.deleteTailReverses' : 'billing.deleteTailPending');
            const question = t('billing.confirmDeleteTx', { num, total: total.toFixed(2) });
            if (!window.confirm(`${question}\n${tail}`)) return;
            try {
              await billingApi.deleteInvoice(id);
              if (selectedAccount) { loadInvoices(selectedAccount); loadAccounts(); }
            } catch (e: any) { alert(e.response?.data?.detail || t('billing.errDelete')); }
          };

          const handleStatusChange = async (id: number, newStatus: string) => {
            if (!window.confirm(t('billing.confirmMarkAs', { status: getStatusLabel(newStatus) }))) return;
            try {
              await billingApi.updateInvoiceStatus(id, newStatus);
              if (selectedAccount) { loadInvoices(selectedAccount); loadAccounts(); }
            } catch (e: any) { alert(e.response?.data?.detail || t('billing.errStatusUpdate')); }
          };

          return (
            <>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'center' }}>
              <select
                className="form-select"
                value={txTypeFilter}
                onChange={e => setTxTypeFilter(e.target.value)}
                style={{ width: 160 }}
              >
                <option value="">{t('billing.allTypes')}</option>
                <option value="payment_confirmation">{t('billing.typeTopup')}</option>
                <option value="topup_request">{t('billing.typeTopupPending')}</option>
                <option value="spend_invoice">{t('billing.typeSpend')}</option>
              </select>
              <select
                className="form-select"
                value={txStatusFilter}
                onChange={e => setTxStatusFilter(e.target.value)}
                style={{ width: 160 }}
              >
                <option value="">{t('billing.allStatuses')}</option>
                <option value="ongoing">{t('billing.st_ongoing')}</option>
                <option value="paid">{t('billing.st_paid')}</option>
                <option value="bonus">{t('billing.st_bonus')}</option>
                <option value="issued">{t('billing.st_issued')}</option>
              </select>
              <input
                type="date"
                className="form-select"
                value={txDateFrom}
                onChange={e => setTxDateFrom(e.target.value)}
                style={{ width: 150 }}
                title={t('billing.periodFrom')}
              />
              <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>—</span>
              <input
                type="date"
                className="form-select"
                value={txDateTo}
                onChange={e => setTxDateTo(e.target.value)}
                style={{ width: 150 }}
                title={t('billing.periodTo')}
              />
              {(txTypeFilter || txStatusFilter || txDateFrom || txDateTo) && (
                <button className="btn-secondary" style={{ fontSize: 12, padding: '6px 12px' }} onClick={() => { setTxTypeFilter(''); setTxStatusFilter(''); setTxDateFrom(''); setTxDateTo(''); }}>
                  {t('billing.clear')}
                </button>
              )}
            </div>
            <div className="users-table-container">
              <table className="users-table">
                <thead>
                  <tr>
                    <th style={{ width: 140 }}>{t('billing.colNumber')}</th>
                    <th style={{ width: 120 }}>{t('billing.colPeriod')}</th>
                    <th style={{ width: 90 }}>{t('billing.colStatus')}</th>
                    <th style={{ width: 130, textAlign: 'right' }}>{t('billing.typeTopup')}</th>
                    <th style={{ width: 130, textAlign: 'right' }}>{t('billing.typeSpend')}</th>
                    <th style={{ width: 100 }}>{t('billing.colDate')}</th>
                    <th style={{ width: 100 }}>{t('billing.colActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map(inv => {
                    const isPureTopup = inv.type === 'payment_confirmation' || inv.type === 'topup_request';
                    const showTopup = isPureTopup;
                    const showSpend = inv.type === 'spend_invoice';
                    const isPending = inv.type === 'topup_request' && inv.status !== 'paid';
                    // TOP-UP / SPEND amounts are always shown NET (без НДС) — they
                    // mirror the balance movement. VAT lives only inside the
                    // invoice document (PDF), never in these columns.
                    const { whole, cents } = fmtAmount(inv.subtotal ?? inv.total);
                    return (
                      <tr key={inv.id}>
                        <td>
                          <span style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 500 }}>{inv.invoice_number}</span>
                        </td>
                        <td style={{ fontSize: 13, whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>
                          {inv.period_start
                            ? new Date(inv.period_start).toLocaleDateString(currentLocale(), { month: 'long', year: 'numeric' })
                            : <span style={{ color: '#ccc' }}>{'\u2014'}</span>}
                        </td>
                        <td>
                          <span style={{
                            display: 'inline-block', padding: '2px 8px', borderRadius: 4,
                            fontSize: 12, fontWeight: 500, background: getStatusColor(inv.status), color: '#fff',
                          }}>
                            {getStatusLabel(inv.status)}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {showTopup ? (
                            <>
                              <span style={{ fontSize: 14, fontWeight: 600, color: isPending ? '#999' : '#27ae60' }}>+{whole}</span>
                              <span style={{ fontSize: 11, color: isPending ? '#999' : '#27ae60' }}>.{cents}</span>
                              {' '}<span style={{ fontSize: 11, color: '#999', fontWeight: 500 }}>{inv.currency}</span>
                              {isPending && (
                                <span style={{ display: 'block', fontSize: 10, color: 'var(--text-tertiary)', fontStyle: 'italic' }}>
                                  {t('billing.awaitingPayment')}
                                </span>
                              )}
                            </>
                          ) : <span style={{ color: '#ccc' }}>{'\u2014'}</span>}
                        </td>
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {showSpend ? (
                            <>
                              <span style={{ fontSize: 14, fontWeight: 600, color: '#e53935' }}>{'\u2212'}{whole}</span>
                              <span style={{ fontSize: 11, color: '#e53935' }}>.{cents}</span>
                              {' '}<span style={{ fontSize: 11, color: '#999', fontWeight: 500 }}>{inv.currency}</span>
                            </>
                          ) : <span style={{ color: '#ccc' }}>{'\u2014'}</span>}
                        </td>
                        <td style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                          {inv.created_at ? fmtDate(inv.created_at) : ''}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                            {inv.status !== 'ongoing' && inv.invoice_number && (
                              <button className="btn-secondary" onClick={() => handleDownloadPdf(inv.id, inv.invoice_number, inv.type)} title={t('billing.downloadPdf')} style={{ padding: '4px 8px', fontSize: 12 }}>
                                PDF
                              </button>
                            )}
                            {isPureTopup && isAdmin && (
                              <button className="btn-secondary" onClick={() => handleDelete(inv.id, inv.invoice_number, inv.total, inv.type, inv.status)} title={t('common.delete')} style={{ padding: '4px 8px', fontSize: 12, color: '#e53935' }}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                                  <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </button>
                            )}
                            {inv.type === 'spend_invoice' && inv.status === 'issued' && isAdmin && (
                              <button className="btn-secondary" onClick={() => handleStatusChange(inv.id, 'paid')} style={{ padding: '4px 8px', fontSize: 12 }}>
                                {t('billing.markPaid')}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {sorted.length === 0 && (
                    <tr>
                      <td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 40 }}>
                        {t('billing.noTransactions')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            </>
          );
        })()}


        {/* Payment Methods tab */}
        {activeTab === 'methods' && (
          <PaymentMethodsManager
            advertiserId={selectedAccount}
            currency={accounts.find(a => a.advertiser_id === selectedAccount)?.currency || 'EUR'}
          />
        )}

        {/* Auto-topup tab */}
        {activeTab === 'auto-topup' && (
          <AutoTopupSettings
            advertiserId={selectedAccount}
            refreshKey={methodsRefreshKey}
          />
        )}

        {/* Manual top-up modal */}
        {showTopup && (
          <div className="users-modal-overlay" onClick={() => setShowTopup(false)}>
            <div className="users-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
              <div className="users-modal-header">
                <h2>{t('billing.manualTopup')}</h2>
                <button className="users-modal-close" onClick={() => setShowTopup(false)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="users-modal-body">
                <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 20 }}>
                  {selectedAccountData.advertiser_name}
                </p>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 6 }}>{t('billing.type')}</label>
                  <div style={{ display: 'flex', gap: 0, borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border-color)' }}>
                    <button
                      type="button"
                      onClick={() => setTopupType('payment')}
                      style={{ flex: 1, padding: '9px 0', fontSize: 13, fontWeight: 500, border: 'none', cursor: 'pointer', background: topupType === 'payment' ? 'var(--accent-gradient)' : 'var(--bg-secondary)', color: topupType === 'payment' ? '#fff' : 'var(--text-secondary)', transition: 'all .15s' }}
                    >{t('billing.amountReceived')}</button>
                    <button
                      type="button"
                      onClick={() => setTopupType('bonus')}
                      style={{ flex: 1, padding: '9px 0', fontSize: 13, fontWeight: 500, border: 'none', borderLeft: '1px solid var(--border-color)', cursor: 'pointer', background: topupType === 'bonus' ? '#e8f5e9' : 'var(--bg-secondary)', color: topupType === 'bonus' ? '#2e7d32' : 'var(--text-secondary)', transition: 'all .15s' }}
                    >{t('billing.bonus')}</button>
                  </div>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 6 }}>{t('billing.amount')}</label>
                  <input
                    type="number"
                    value={topupAmount}
                    onChange={e => setTopupAmount(e.target.value)}
                    placeholder="0.00"
                    min="0.01"
                    step="0.01"
                    autoFocus
                    style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--border-color)', borderRadius: 10, fontSize: 14, outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' as const }}
                  />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 6 }}>{t('billing.descriptionOptional')}</label>
                  <input
                    type="text"
                    value={topupDesc}
                    onChange={e => setTopupDesc(e.target.value)}
                    placeholder={t(topupType === 'bonus' ? 'billing.phBonusDesc' : 'billing.phWireDesc')}
                    style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--border-color)', borderRadius: 10, fontSize: 14, outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' as const }}
                  />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 6 }}>{t('billing.colDate')}</label>
                  <input
                    type="date"
                    value={topupDate}
                    onChange={e => setTopupDate(e.target.value)}
                    style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--border-color)', borderRadius: 10, fontSize: 14, outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' as const }}
                  />
                </div>
              </div>
              <div className="users-modal-footer">
                <button className="users-btn users-btn-secondary" onClick={() => setShowTopup(false)}>{t('common.cancel')}</button>
                <button
                  className="users-btn"
                  style={{ background: topupType === 'bonus' ? '#2e7d32' : 'var(--accent-gradient)', color: '#fff', border: 'none', opacity: !topupAmount || parseFloat(topupAmount) <= 0 ? 0.5 : 1, cursor: !topupAmount || parseFloat(topupAmount) <= 0 ? 'not-allowed' : 'pointer' }}
                  onClick={handleTopup}
                  disabled={!topupAmount || parseFloat(topupAmount) <= 0}
                >
                  {t(topupType === 'bonus' ? 'billing.addBonus' : 'billing.confirmTopup')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Card top-up modal — opens Revolut hosted checkout in new tab */}
        {showCardTopup && (
          <TopUpDialog
            advertiserId={selectedAccount}
            currency={accounts.find(a => a.advertiser_id === selectedAccount)?.currency || 'EUR'}
            onClose={() => setShowCardTopup(false)}
            onCheckoutOpened={handleCardCheckoutOpened}
            cardAvailable={!!selectedAccountData?.card_available}
            bankAvailable={!!selectedAccountData?.bank_available}
          />
        )}
      </>
    );
  }

  // ── List view (accounts table) ──
  return (
    <>
      <div className="header">
        <div className="header-title">
          <h1>{t('billing.title')}</h1>
          <p className="header-subtitle">{t('billing.subtitle')}</p>
        </div>
      </div>

      {error && (
        <div style={{ background: 'rgba(231, 76, 60, 0.08)', color: '#e74c3c', padding: '12px 16px', borderRadius: 10, marginBottom: 20, fontSize: 13, fontWeight: 500 }}>
          {error}
        </div>
      )}

      <div className="reports-layout">
        {/* Sidebar — Filters only */}
        <aside className="filters-panel">
          <div className="filter-section">
            <h3 className="section-heading">{t('common.search')}</h3>
            <div className="campaign-search-input-wrapper">
              <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                className="campaign-search-input"
                placeholder={t('billing.searchNameId')}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {isAdmin && (
            <div className="filter-section">
              <h3 className="section-heading">{t('clients.regionalOffice')}</h3>
              <select className="form-select" value={officeFilter} onChange={e => setOfficeFilter(e.target.value)}>
                <option value="">{t('clients.allOffices')}</option>
                {officeOptions.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          )}

          <div className="filter-section">
            <h3 className="section-heading">{t('clients.billingModel')}</h3>
            <select className="form-select" value={modelFilter} onChange={e => setModelFilter(e.target.value)}>
              <option value="">{t('billing.allModels')}</option>
              <option value="prepaid">{t('billing.bm_prepaid')}</option>
              <option value="postpaid">{t('billing.bm_postpaid')}</option>
            </select>
          </div>

          <div className="filter-section">
            <h3 className="section-heading">{t('billing.colStatus')}</h3>
            <select className="form-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="">{t('billing.allStatuses')}</option>
              <option value="active">{t('billing.statusActive')}</option>
              <option value="paused">{t('billing.statusPaused')}</option>
              <option value="auto-pause">{t('billing.autoPauseOn')}</option>
            </select>
          </div>

          {isSuperAdmin && (
            <div className="filter-section">
              <h3 className="section-heading">{t('billing.view')}</h3>
              <select className="form-select" value={showHidden ? 'hidden' : 'active'} onChange={e => setShowHidden(e.target.value === 'hidden')}>
                <option value="active">{t('billing.statusActive')}</option>
                <option value="hidden">{t('billing.viewHidden', { count: accounts.filter(a => a.hidden).length })}</option>
              </select>
            </div>
          )}

          {hasFilters && (
            <div className="filter-section" style={{ marginTop: 8 }}>
              <button className="btn-secondary" style={{ width: '100%' }} onClick={clearFilters}>
                {t('billing.clearFilters')}
              </button>
            </div>
          )}
        </aside>

        {/* Main — Accounts table */}
        <div className="results-area visible">
          {filteredAccounts.length === 0 ? (
            <div className="editor-empty">
              <div className="editor-empty-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
                  <rect x="2" y="3" width="20" height="18" rx="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 9h20" strokeLinecap="round"/>
                  <path d="M9 21V9" strokeLinecap="round"/>
                </svg>
              </div>
              <h3>{t(hasFilters ? 'billing.noAccountsMatch' : 'billing.noBillingAccounts')}</h3>
              <p>{t(hasFilters ? 'billing.tryAdjusting' : 'billing.accountsAppear')}</p>
            </div>
          ) : (
            <div className="users-table-container">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>{t('billing.colAdvertiser')}</th>
                    <th style={{ width: 100 }}>{t('billing.colModel')}</th>
                    <th style={{ width: 140, textAlign: 'right' }}>{t('billing.colBalance')}</th>
                    <th style={{ width: 110 }}>{t('billing.colStatus')}</th>
                    <th style={{ width: 100 }}>{t('billing.colActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAccounts.map(a => (
                    <tr
                      key={a.advertiser_id}
                      onClick={() => setSelectedAccount(a.advertiser_id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <span className="users-user-name" style={{ fontSize: 13 }}>{a.advertiser_name || a.advertiser_id}</span>
                        <span className="users-user-email" style={{ display: 'block' }}>{a.office_name || 'Central Hub'}</span>
                      </td>
                      <td>
                        <span style={modelBadgeStyle(a.billing_model)}>
                          {t(`billing.bm_${a.billing_model}`, { defaultValue: a.billing_model })}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 600, fontSize: 14, color: a.balance <= 0 ? '#e74c3c' : '#27ae60' }}>
                        {formatCurrency(a.balance, a.currency)}
                      </td>
                      <td>
                        {a.is_paused_by_billing && (
                          <span style={paymentStatusStyle('failed')}>{t('billing.statusPaused')}</span>
                        )}
                        {a.auto_pause_enabled && !a.is_paused_by_billing && (
                          <span style={paymentStatusStyle('succeeded')}>{t('billing.autoPause')}</span>
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {canSelfTopup && a.payment_available && (
                            <button
                              style={{
                                background: 'var(--accent-gradient)',
                                color: '#fff',
                                border: 'none',
                                padding: '5px 12px',
                                fontSize: 12,
                                borderRadius: 8,
                                cursor: 'pointer',
                                fontWeight: 600,
                              }}
                              onClick={(e) => { e.stopPropagation(); setSelectedAccount(a.advertiser_id); setShowCardTopup(true); }}
                            >
                              {t('billing.topUp')}
                            </button>
                          )}
                          {canManualTopup && (
                            <button
                              className="users-btn users-btn-small users-btn-secondary"
                              style={{ padding: '5px 12px', fontSize: 12, borderRadius: 8 }}
                              onClick={(e) => { e.stopPropagation(); setSelectedAccount(a.advertiser_id); setShowTopup(true); }}
                            >
                              {t('billing.manual')}
                            </button>
                          )}
                          {isSuperAdmin && (
                            <button
                              className="users-btn users-btn-small users-btn-secondary"
                              style={{ padding: '5px 12px', fontSize: 12, borderRadius: 8 }}
                              onClick={(e) => handleHideToggle(a, e)}
                              title={t(a.hidden ? 'billing.unhideFromList' : 'billing.hideFromList')}
                            >
                              {t(a.hidden ? 'billing.unhide' : 'billing.hide')}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Manual top-up modal (from list) */}
      {showTopup && selectedAccount && (
        <div className="users-modal-overlay" onClick={() => { setShowTopup(false); setSelectedAccount(null); }}>
          <div className="users-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <div className="users-modal-header">
              <h2>{t('billing.manualTopup')}</h2>
              <button className="users-modal-close" onClick={() => { setShowTopup(false); setSelectedAccount(null); }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="users-modal-body">
              <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 20 }}>
                {accounts.find(a => a.advertiser_id === selectedAccount)?.advertiser_name}
              </p>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 6 }}>{t('billing.type')}</label>
                <div style={{ display: 'flex', gap: 0, borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border-color)' }}>
                  <button type="button" onClick={() => setTopupType('payment')}
                    style={{ flex: 1, padding: '9px 0', fontSize: 13, fontWeight: 500, border: 'none', cursor: 'pointer', background: topupType === 'payment' ? 'var(--accent-gradient)' : 'var(--bg-secondary)', color: topupType === 'payment' ? '#fff' : 'var(--text-secondary)', transition: 'all .15s' }}
                  >{t('billing.amountReceived')}</button>
                  <button type="button" onClick={() => setTopupType('bonus')}
                    style={{ flex: 1, padding: '9px 0', fontSize: 13, fontWeight: 500, border: 'none', borderLeft: '1px solid var(--border-color)', cursor: 'pointer', background: topupType === 'bonus' ? '#e8f5e9' : 'var(--bg-secondary)', color: topupType === 'bonus' ? '#2e7d32' : 'var(--text-secondary)', transition: 'all .15s' }}
                  >{t('billing.bonus')}</button>
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 6 }}>{t('billing.amount')}</label>
                <input type="number" value={topupAmount} onChange={e => setTopupAmount(e.target.value)} placeholder="0.00" min="0.01" step="0.01" autoFocus
                  style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--border-color)', borderRadius: 10, fontSize: 14, outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' as const }} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 6 }}>{t('billing.descriptionOptional')}</label>
                <input type="text" value={topupDesc} onChange={e => setTopupDesc(e.target.value)} placeholder={t(topupType === 'bonus' ? 'billing.phBonusDesc' : 'billing.phWireDesc')}
                  style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--border-color)', borderRadius: 10, fontSize: 14, outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' as const }} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 6 }}>{t('billing.colDate')}</label>
                <input type="date" value={topupDate} onChange={e => setTopupDate(e.target.value)}
                  style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--border-color)', borderRadius: 10, fontSize: 14, outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' as const }} />
              </div>
            </div>
            <div className="users-modal-footer">
              <button className="users-btn users-btn-secondary" onClick={() => { setShowTopup(false); setSelectedAccount(null); }}>{t('common.cancel')}</button>
              <button className="users-btn"
                style={{ background: topupType === 'bonus' ? '#2e7d32' : 'var(--accent-gradient)', color: '#fff', border: 'none', opacity: !topupAmount || parseFloat(topupAmount) <= 0 ? 0.5 : 1, cursor: !topupAmount || parseFloat(topupAmount) <= 0 ? 'not-allowed' : 'pointer' }}
                onClick={handleTopup} disabled={!topupAmount || parseFloat(topupAmount) <= 0}>
                {t(topupType === 'bonus' ? 'billing.addBonus' : 'billing.confirmTopup')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Card top-up modal (from list) */}
      {showCardTopup && selectedAccount && (() => {
        const acc = accounts.find(a => a.advertiser_id === selectedAccount);
        return (
          <TopUpDialog
            advertiserId={selectedAccount}
            currency={acc?.currency || 'EUR'}
            onClose={() => { setShowCardTopup(false); setSelectedAccount(null); }}
            onCheckoutOpened={handleCardCheckoutOpened}
            cardAvailable={!!acc?.card_available}
            bankAvailable={!!acc?.bank_available}
          />
        );
      })()}
    </>
  );
};

export default BillingPage;
